import styled from '@emotion/styled'
import { useTranslation } from 'react-i18next'

import { AnalyticsEvent, useEventAnalytics } from 'src/hooks/use-analytics'

export type CaptionTrack = {
  lang: string
  url: string
}

// A video file we host (a coaching resource in the `video` format), played in
// the browser's own player with one caption track per language. The video and
// its captions live on another origin, so the element is `crossOrigin`: a
// cross-origin <track> only loads that way, and the CDN answers both the MP4
// and the VTT with the CORS headers it needs.
export function HostedVideo(props: {
  id: string
  url: string
  poster?: string | null
  captions: CaptionTrack[]
  label?: string | null
  title?: string | null
}) {
  const { t, i18n } = useTranslation()
  const videoPlayEvent = useEventAnalytics(AnalyticsEvent.videoPlay)
  const videoPauseEvent = useEventAnalytics(AnalyticsEvent.videoPause)
  const videoEndEvent = useEventAnalytics(AnalyticsEvent.videoEnd)

  const analyticProperties = {
    video_id: props.id,
    video_url: props.url,
    ...(props.title == null ? {} : { title: props.title }),
    ...(props.label == null ? {} : { label: props.label })
  }

  const defaultLang = defaultCaptionLang(props.captions, i18n.language)

  const captionLabel = (lang: string) => {
    const key = `common:language.${baseLang(lang)}`
    return i18n.exists(key) ? t(key) : lang
  }

  return (
    <Box>
      <video
        controls
        crossOrigin='anonymous'
        preload='metadata'
        playsInline
        src={props.url}
        poster={props.poster ?? undefined}
        title={props.title ?? undefined}
        onPlay={() => {
          videoPlayEvent(analyticProperties)
        }}
        onPause={() => {
          videoPauseEvent(analyticProperties)
        }}
        onEnded={() => {
          videoEndEvent(analyticProperties)
        }}
      >
        {props.captions.map((track) => (
          <track
            key={track.lang}
            kind='subtitles'
            srcLang={track.lang}
            src={track.url}
            label={captionLabel(track.lang)}
            default={track.lang === defaultLang}
          />
        ))}
      </video>
    </Box>
  )
}

// The track shown by default: the user's language, else English, else none.
export function defaultCaptionLang(
  captions: CaptionTrack[],
  language: string
): string | null {
  const wanted = baseLang(language)
  const match =
    captions.find((track) => baseLang(track.lang) === wanted) ??
    captions.find((track) => baseLang(track.lang) === 'en')
  return match == null ? null : match.lang
}

const baseLang = (lang: string) => lang.toLowerCase().split('-')[0]

const Box = styled.div`
  video {
    display: block;
    width: 100%;
    aspect-ratio: 16 / 9;
    background: #000;
  }
`
